import { BrandLogo } from '../components/Brand';
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { leaderboardAPI } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
    Scale, 
    Trophy, 
    Crown,
    Medal,
    Award,
    ArrowLeft,
    TrendingUp,
    Target,
    User
} from "lucide-react";

export default function LeaderboardPage() {
    const { user } = useAuth();
    const [leaderboard, setLeaderboard] = useState([]);
    const [myRank, setMyRank] = useState(null);
    const [loading, setLoading] = useState(true);
    
    useEffect(() => {
        loadLeaderboard();
    }, []);
    
    const loadLeaderboard = async () => {
        try {
            const [leaderboardRes, myRankRes] = await Promise.all([
                leaderboardAPI.getLeaderboard(50),
                user ? leaderboardAPI.getMyRank() : Promise.resolve({ data: null })
            ]);
            setLeaderboard(leaderboardRes.data);
            setMyRank(myRankRes.data);
        } catch (error) {
            console.error("Failed to load leaderboard:", error);
        } finally {
            setLoading(false);
        }
    };
    
    const getRankIcon = (rank) => {
        switch(rank) {
            case 1:
                return <Crown className="w-6 h-6 text-yellow-400" />;
            case 2:
                return <Medal className="w-6 h-6 text-slate-300" />;
            case 3:
                return <Award className="w-6 h-6 text-orange-400" />;
            default:
                return <span className="w-6 text-center font-mono text-slate-500">{rank}</span>;
        }
    };
    
    const getRankClass = (rank) => {
        switch(rank) {
            case 1:
                return "border-yellow-500/30 bg-yellow-500/5";
            case 2:
                return "border-slate-400/30 bg-slate-400/5";
            case 3:
                return "border-orange-500/30 bg-orange-500/5";
            default:
                return "border-slate-800";
        }
    };
    
    return (
        <div className="min-h-screen bg-background">
            {/* Navigation */}
            <nav className="sticky top-0 z-50 glass border-b border-slate-800 px-6 py-4">
                <div className="max-w-4xl mx-auto flex items-center justify-between">
                    <Link to={user ? "/dashboard" : "/"} className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors">
                        <ArrowLeft className="w-5 h-5" />
                        <span className="hidden sm:inline">Back</span>
                    </Link>
                    
                    <BrandLogo />
                    
                    <div className="w-20" />
                </div>
            </nav>
            
            <main className="max-w-4xl mx-auto px-6 py-8">
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-center mb-8"
                >
                    <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
                        <Trophy className="w-8 h-8 text-amber-400" />
                    </div>
                    <h1 className="font-heading text-3xl sm:text-4xl font-bold text-white mb-2">
                        Leaderboard
                    </h1>
                    <p className="text-slate-400">Top legal minds competing for glory</p>
                </motion.div>
                
                {/* My Rank Card */}
                {myRank && (
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                        className="mb-6"
                    >
                        <Card className="glass border-primary/30 rounded-2xl" data-testid="my-rank-card">
                            <CardContent className="p-4 sm:p-6">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-4">
                                        <div className="w-12 h-12 rounded-full bg-primary/20 flex items-center justify-center">
                                            <User className="w-6 h-6 text-primary" />
                                        </div>
                                        <div>
                                            <p className="text-white font-semibold">{myRank.username}</p>
                                            <p className="text-slate-400 text-sm">Your Position</p>
                                        </div>
                                    </div>
                                    <div className="text-right">
                                        <p className="font-heading text-3xl font-bold gradient-text">#{myRank.rank}</p>
                                        <p className="text-slate-400 text-sm">{myRank.total_score.toLocaleString()} pts</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </motion.div>
                )}
                
                {/* Leaderboard List */}
                {loading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : leaderboard.length === 0 ? (
                    <Card className="glass border-slate-800 rounded-2xl">
                        <CardContent className="p-8 text-center">
                            <Trophy className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400 mb-4">No rankings yet. Be the first to play!</p>
                            {user && (
                                <Link to="/play">
                                    <Button className="rounded-full bg-[#299dce] hover:bg-[#299dce]/90">
                                        Start Playing
                                    </Button>
                                </Link>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.2 }}
                        className="space-y-3"
                        data-testid="leaderboard-list"
                    >
                        {/* Top 3 Podium */}
                        {leaderboard.length >= 3 && (
                            <div className="grid grid-cols-3 gap-3 mb-6">
                                {/* 2nd Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.3 }}
                                    className="order-1"
                                >
                                    <Card className="glass border-slate-400/30 bg-slate-400/5 rounded-2xl h-full">
                                        <CardContent className="p-4 text-center">
                                            <Medal className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                                            <p className="font-heading text-2xl font-bold text-white">2nd</p>
                                            <p className="text-white font-medium truncate">{leaderboard[1].username}</p>
                                            <p className="text-slate-400 text-sm font-mono">{leaderboard[1].total_score.toLocaleString()}</p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                                
                                {/* 1st Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.2 }}
                                    className="order-0 sm:order-1 col-span-3 sm:col-span-1 -mt-4"
                                >
                                    <Card className="glass border-yellow-500/30 bg-yellow-500/5 rounded-2xl">
                                        <CardContent className="p-6 text-center">
                                            <Crown className="w-10 h-10 text-yellow-400 mx-auto mb-2" />
                                            <p className="font-heading text-3xl font-bold gradient-text">1st</p>
                                            <p className="text-white font-semibold text-lg">{leaderboard[0].username}</p>
                                            <p className="text-yellow-400 font-mono font-bold">{leaderboard[0].total_score.toLocaleString()}</p>
                                            <Badge className="mt-2 bg-yellow-500/20 text-yellow-400 border-0">
                                                Lvl {leaderboard[0].level}
                                            </Badge>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                                
                                {/* 3rd Place */}
                                <motion.div
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: 0.4 }}
                                    className="order-2"
                                >
                                    <Card className="glass border-orange-500/30 bg-orange-500/5 rounded-2xl h-full">
                                        <CardContent className="p-4 text-center">
                                            <Award className="w-8 h-8 text-orange-400 mx-auto mb-2" />
                                            <p className="font-heading text-2xl font-bold text-white">3rd</p>
                                            <p className="text-white font-medium truncate">{leaderboard[2].username}</p>
                                            <p className="text-slate-400 text-sm font-mono">{leaderboard[2].total_score.toLocaleString()}</p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            </div>
                        )}
                        
                        {/* Rest of Leaderboard */}
                        {leaderboard.slice(3).map((entry, index) => (
                            <motion.div
                                key={entry.user_id}
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: 0.1 * (index + 5) }}
                            >
                                <Card className={`glass rounded-xl ${
                                    user && entry.user_id === user.id ? "border-primary/50" : "border-slate-800"
                                }`}>
                                    <CardContent className="p-4">
                                        <div className="flex items-center gap-4">
                                            <span className="w-8 text-center font-mono text-slate-500 font-bold">
                                                {entry.rank}
                                            </span>
                                            <div className="flex-1 min-w-0">
                                                <p className={`font-medium truncate ${
                                                    user && entry.user_id === user.id ? "text-primary" : "text-white"
                                                }`}>
                                                    {entry.username}
                                                </p>
                                                <div className="flex items-center gap-3 text-xs text-slate-500">
                                                    <span>{entry.games_played} games</span>
                                                    <span>{entry.accuracy}% accuracy</span>
                                                </div>
                                            </div>
                                            <div className="text-right">
                                                <p className="font-mono font-bold text-white">
                                                    {entry.total_score.toLocaleString()}
                                                </p>
                                                <Badge className="bg-slate-800 text-slate-400 border-0 text-xs">
                                                    Lvl {entry.level}
                                                </Badge>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </motion.div>
                        ))}
                    </motion.div>
                )}
                
                {/* CTA for non-logged in users */}
                {!user && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 0.5 }}
                        className="mt-8 text-center"
                    >
                        <Link to="/register">
                            <Button size="lg" className="rounded-full bg-[#299dce] hover:bg-[#299dce]/90">
                                Join the Competition
                            </Button>
                        </Link>
                    </motion.div>
                )}
            </main>
        </div>
    );
}
